import Hashids from 'hashids'

import { Form, GetAppShowResponse, GetBadgeGroupsShowResponse, GetCollectionItemsIndexResponse, GetCollectionsShowResponse, GetMediaItemsShowResponse, GetQuizzesShowResponse, GetScreensShowResponse, Manifest } from 'mytours-read-api-typescript'

export default class STQRYBunnyReadApi {
    host: string
    parentProjectId: number
    tokenType: string
    tokenUUID: string
    hashids: Hashids

    constructor(host: string, parentProjectId: number, tokenType: string, tokenUUID: string, publishingCryptKey: string) {
        this.host = host
        this.parentProjectId = parentProjectId
        this.tokenType = tokenType
        this.tokenUUID = tokenUUID
        // `hFCSfUiIHtuTsc` seems to be the default Ruby `seps`
        this.hashids = new Hashids(publishingCryptKey, 10, "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789", "hFCSfUiIHtuTsc")
    }

    async fetchApp(accountId: number): Promise<GetAppShowResponse> {
        return this.fetchResource(accountId, `/app.json?t=${Date.now()}`)
    }

    async fetchManifest(accountId: number): Promise<Manifest> {
        return this.fetchResource(accountId, `/manifest.json?t=${Date.now()}`)
    }

    async fetchScreen(accountId: number, screenId: number, screenVersion: number, language: string): Promise<GetScreensShowResponse> {
        return this.fetchResource(accountId, `/screens/${this.hashids.encode(screenId)}/metadata-${language}.json?t=${screenVersion}`, true)
    }

    async fetchCollection(accountId: number, collectionId: number, collectionVersion: number, language: string): Promise<GetCollectionsShowResponse> {
        return this.fetchResource(accountId, `/collections/${this.hashids.encode(collectionId)}/metadata-${language}.json?t=${collectionVersion}`, true)
    }

    async fetchCollectionItems(accountId: number, collectionId: number, collectionVersion: number, language: string): Promise<GetCollectionItemsIndexResponse> {
        return this.fetchResource(accountId, `/collections/${this.hashids.encode(collectionId)}/items/metadata-${language}.json?t=${collectionVersion}`, true)
    }

    async fetchMapRoute(accountId: number, mapRouteId: number, mapRouteVersion: number, language: string): Promise<Form> {
        return this.fetchResource(accountId, `/map_routes/${this.hashids.encode(mapRouteId)}/metadata-${language}.json?t=${mapRouteVersion}`, true)
    }

    async fetchMediaItem(accountId: number, mediaItemId: number, mediaItemVersion: number, language: string): Promise<GetMediaItemsShowResponse> {
        return this.fetchResource(accountId, `/media_items/${this.hashids.encode(mediaItemId)}/metadata-${language}.json?t=${mediaItemVersion}`, true)
    }

    async fetchQuiz(accountId: number, quizId: number, quizVersion: number, language: string): Promise<GetQuizzesShowResponse> {
        return this.fetchResource(accountId, `/quizzes/${this.hashids.encode(quizId)}/metadata-${language}.json?t=${quizVersion}`, true)
    }

    async fetchBadgeGroup(accountId: number, badgeGroupId: number, badgeGroupVersion: number, language: string): Promise<GetBadgeGroupsShowResponse> {
        return this.fetchResource(accountId, `/badge_groups/${this.hashids.encode(badgeGroupId)}/metadata-${language}.json?t=${badgeGroupVersion}`, true)
    }

    async fetchForm(accountId: number, formId: number, formVersion: number, language: string): Promise<Form> {
        return this.fetchResource(accountId, `/crm/forms/${this.hashids.encode(formId)}/metadata-${language}.json?t=${formVersion}`, true)
    }

    private async fetchResource(accountId: number, path: string, fallbackToPrimary: boolean = false) {
        const url = `${this.host}/${this.projectAndAccountPrefix(accountId)}${path}`
        const resourceResponse = await this.fetch(url)

        if (resourceResponse.status === 200) {
            return resourceResponse.json()
        } else if (fallbackToPrimary) {
            const primaryResponse = await this.fetch(url.replace(/-(.*?)(?=\.)/, ''))
            if (primaryResponse.status === 200) {
                return primaryResponse.json()
            } else {
                throw primaryResponse;
            }
        } else {
            throw resourceResponse;
        }
    }

    private projectAndAccountPrefix(accountId: number) {
        return `${this.hashids.encode(this.parentProjectId)}/${this.hashids.encode(accountId)}`
    }

    private fetch(input: RequestInfo | URL, init: RequestInit = {}) {
        return fetch(input, {
            ...init,
            headers: {
                ...(init.headers || {}),
                Authorization: 'Basic ' + btoa(this.parentProjectId + ':' + this.tokenUUID),
                'stqry-project-type': this.tokenType
            }
        })
    }
}
